import React, { useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Pagination from 'components_new/atoms/Pagination'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleTable from 'components_new/molecules/SimpleTable'

import { FileDelivery } from 'redux/selectors/internal/DataRuns'
import {
  deliveryKey,
  deliveryLabel,
  deliveryStatusLabel,
  DELIVERY_STATUS_COLOR,
  formatDayClock,
  formatFileCount,
  formatIntervalHours,
  formatOverdueHours,
  isDeliveryIncomplete,
  isDeliveryProblem,
  STREAK_HIGHLIGHT_THRESHOLD
} from '../utils'

const PAGE_SIZE = 20

/**
 * Why the Intervall column is not a promise, and why most sources have no
 * interval at all. Mandatory: without it the column reads as an agreed
 * schedule, which is exactly what it is not.
 */
const INTERVAL_TOOLTIP =
  'Härlett ur källans egen historik, inte ur ett avtalat intervall. En ' +
  'källa måste ha levererat minst fem gånger med jämna mellanrum för att ' +
  'räknas som återkommande — engångsfiler och oregelbundna källor kan ' +
  'därför aldrig bli försenade eller uteblivna. En källa som aldrig ' +
  'levererat enligt överenskommelse ser också korrekt ut här.'

export interface DataRunsFileDeliveriesProps {
  fileDeliveries: FileDelivery[]
  /**
   * Customer scope shows every file source including the healthy ones -
   * "levererar den här kunden allt de lovat" is the question once a
   * customer is picked. The all-customers view shows only problems, since
   * ~45 customers' worth of green is a wall rather than information.
   */
  isCustomerScope: boolean
  onSelectFileDelivery: (delivery: FileDelivery) => void
}

const COLUMNS = [
  { title: 'Status', width: 120 },
  { title: 'Filkälla' },
  { title: 'Kund' },
  { title: 'Senaste leverans', align: 'right' as const, width: 120 },
  { title: 'Väntad', align: 'right' as const, width: 120 },
  { title: 'Intervall', align: 'right' as const, width: 110 },
  { title: 'I rad', align: 'center' as const, width: 56 },
  { title: '', width: 24 }
]

/**
 * "Filleveranser" - the files customers send us over SFTP, by email or by
 * uploading them in the portal, all three of which are recorded in
 * `connection_db.histories`.
 *
 * This is also the only place late and missing deliveries surface in the
 * all-customers view: the timeline and the 14-day grid both render in
 * customer scope only.
 */
const DataRunsFileDeliveries = (props: DataRunsFileDeliveriesProps) => {
  const { fileDeliveries, isCustomerScope, onSelectFileDelivery } = props

  const [page, setPage] = useState(1)

  const visibleDeliveries = useMemo(
    () =>
      isCustomerScope
        ? fileDeliveries
        : fileDeliveries.filter((delivery) =>
          isDeliveryProblem(delivery.status)
        ),
    [fileDeliveries, isCustomerScope]
  )

  const problemCount = useMemo(
    () =>
      fileDeliveries.filter((delivery) => isDeliveryProblem(delivery.status))
        .length,
    [fileDeliveries]
  )

  const pageCount = Math.max(1, Math.ceil(visibleDeliveries.length / PAGE_SIZE))
  const pagedDeliveries = visibleDeliveries.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  )

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px',

        // Same reason as the fail list: the column widths are narrower than
        // the two-word header labels plus the cell's own padding.
        '& thead th': { whiteSpace: 'nowrap' }
      }}
    >
      <SectionHeader
        icon={
          <Box
            sx={{
              bgcolor: problemCount > 0 ? 'warning.main' : 'success.main',
              borderRadius: '2px',
              height: '10px',
              width: '10px'
            }}
          />
        }
        title="Filleveranser från kund"
        titleAdornment={
          <Chip
            color={problemCount > 0 ? 'warning' : 'default'}
            label={String(problemCount)}
            size="small"
          />
        }
      />

      {visibleDeliveries.length === 0 ? (
        <EmptyPlaceholder
          title={
            fileDeliveries.length === 0
              ? 'Inga filkällor för den här kunden.'
              : 'Alla filkällor levererar som väntat.'
          }
        />
      ) : (
        <>
          <SimpleTable
            columns={COLUMNS}
            disableSorting
            rows={pagedDeliveries.map((delivery) => ({
              id: deliveryKey(delivery),
              onClick: () => onSelectFileDelivery(delivery),
              cells: [
                <Tag
                  color={DELIVERY_STATUS_COLOR[delivery.status]}
                  key="status"
                  label={deliveryStatusLabel(delivery)}
                />,
                <Box key="source" sx={{ minWidth: 0 }}>
                  <Text
                    sx={{
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    variant="body2"
                  >
                    {deliveryLabel(delivery)}
                  </Text>
                  <Text sx={{ color: 'text.disabled' }} variant="caption">
                    {delivery.source_name}
                    {formatFileCount(
                      delivery.file_count,
                      delivery.expected_file_count
                    )
                      ? ' · '
                      : ''}
                    <Text
                      component="span"
                      sx={{
                        color: isDeliveryIncomplete(delivery)
                          ? 'warning.text'
                          : 'text.disabled',
                        fontWeight: isDeliveryIncomplete(delivery) ? 600 : 400
                      }}
                      variant="caption"
                    >
                      {formatFileCount(
                        delivery.file_count,
                        delivery.expected_file_count
                      )}
                    </Text>
                  </Text>
                </Box>,
                <Text
                  key="customer"
                  sx={{
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  variant="body2"
                >
                  {delivery.customer?.name ?? 'Okänd kund'}
                </Text>,
                <Text
                  key="last"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  variant="body2"
                >
                  {formatDayClock(delivery.last_delivery_at)}
                </Text>,
                <Box key="expected">
                  <Text
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    variant="body2"
                  >
                    {formatDayClock(delivery.expected_next_delivery_at)}
                  </Text>
                  {formatOverdueHours(delivery.overdue_hours) ? (
                    <Text sx={{ color: 'warning.text' }} variant="caption">
                      {formatOverdueHours(delivery.overdue_hours)}
                    </Text>
                  ) : null}
                </Box>,
                <Text
                  key="interval"
                  sx={{ color: 'text.secondary' }}
                  variant="body2"
                >
                  {formatIntervalHours(delivery.median_interval_hours)}
                </Text>,
                <Box
                  key="streak"
                  sx={{
                    bgcolor:
                      delivery.consecutive_failures
                        >= STREAK_HIGHLIGHT_THRESHOLD
                        ? 'error.transparent'
                        : undefined,
                    borderRadius: '8px',
                    display: 'inline-block',
                    fontVariantNumeric: 'tabular-nums',
                    px: '6px'
                  }}
                >
                  <Text
                    component="span"
                    sx={{
                      color:
                        delivery.consecutive_failures >=
                        STREAK_HIGHLIGHT_THRESHOLD
                          ? 'error.text'
                          : 'text.primary',
                      fontWeight: 600
                    }}
                    variant="body2"
                  >
                    {delivery.consecutive_failures}×
                  </Text>
                </Box>,
                <Icon
                  key="chevron"
                  fontSize="small"
                  name="ArrowForwardIos"
                  sx={{ color: 'text.disabled' }}
                />
              ]
            }))}
          />
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mt: 1
            }}
          >
            <Text sx={{ color: 'text.disabled' }} variant="caption">
              {isCustomerScope
                ? `${fileDeliveries.length - problemCount} av ` +
                  `${fileDeliveries.length} filkällor levererar som väntat.`
                : `${problemCount} filkällor behöver ses över.`}
            </Text>
            <Tooltip title={INTERVAL_TOOLTIP}>
              <Text
                sx={{
                  color: 'text.disabled',
                  cursor: 'help',
                  textDecoration: 'underline dotted'
                }}
                variant="caption"
              >
                Om intervallen
              </Text>
            </Tooltip>
          </Box>
          {pageCount > 1 ? (
            <Pagination
              count={pageCount}
              onChange={(_, newPage) => setPage(newPage)}
              page={page}
              shape="rounded"
              sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
              variant="outlined"
            />
          ) : null}
        </>
      )}
    </Box>
  )
}

DataRunsFileDeliveries.displayName = 'DataRunsFileDeliveries'
export default DataRunsFileDeliveries
